import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Loader2, Search } from "lucide-react";

export default function DrugAutocomplete({ value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      const q = (value || "").trim();
      if (q.length < 2) {
        setItems([]);
        return;
      }
      setLoading(true);
      try {
        const { data } = await api.get(`/drugs/search`, { params: { q } });
        setItems(data.items || []);
        setActive(0);
      } catch (_e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (name) => {
    onChange(name);
    setOpen(false);
  };

  return (
    <div ref={wrap} className="relative">
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: "#5C6B66" }}
        />
        <Input
          data-testid="add-med-name-input"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (!open || items.length === 0) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, items.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              pick(items[active]);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
          placeholder={placeholder || "Start typing a drug or supplement…"}
          className="h-11 rounded-xl pl-9"
          autoComplete="off"
        />
        {loading && (
          <Loader2
            size={14}
            className="animate-spin absolute right-3 top-1/2 -translate-y-1/2"
            style={{ color: "#5C6B66" }}
          />
        )}
      </div>

      {open && items.length > 0 && (
        <div
          data-testid="drug-autocomplete-list"
          className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-auto rounded-xl border shadow-lg"
          style={{ background: "#FFFFFF", borderColor: "#E5DEC9" }}
        >
          {items.map((name, i) => (
            <button
              type="button"
              key={name}
              data-testid={`drug-suggestion-${i}`}
              onClick={() => pick(name)}
              onMouseEnter={() => setActive(i)}
              className="w-full text-left px-3 py-2 text-sm block"
              style={{
                background: i === active ? "#F4EFE6" : "transparent",
                color: "#1C2826",
              }}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
